/**
 * Footnote-marker normalization for migrated article HTML.
 *
 * The migration export stores the same reference marker in a handful of shapes,
 * and only some of them are already an interactive `<sup>`:
 *
 *  - `… the Kaʿba.<sup>[8]</sup>` — bracketed superscript (renders, needs wiring)
 *  - `… the Amun temple<a href="#_ftn7" name="_ftnref7">[7]</a>` — legacy anchor
 *  - `<a href="#_ftn6" name="_ftnref6"><sup>[6]</sup></a>` — anchor around a sup
 *  - `<a href="#_ftn7" name="_ftnref7"></a>` — the *back*-link left behind next to
 *    a marker that is already a `<sup>` (nothing to show)
 *  - `… the Kaʿba.<sup>8</sup>` — a superscript with no brackets
 *
 * A DOM text walk skips text inside `<a>`/`<sup>`, so every non-plain-text shape
 * used to leak through as inert bracketed text (or, for the empty back-link, was
 * "promoted" into a duplicate marker). These helpers normalize all of them to a
 * single `<sup>[N]</sup>` so one wiring pass in `FootnotesSection` handles them.
 */

/** Anchors the old site exported for its footnote references. */
const LEGACY_ANCHOR_SELECTOR =
	'a[href^="#_ftn"], a[name^="_ftnref"], a[href*="thetorah.com/#_ftn"]'

/** Bracketed marker, e.g. `[7]`. */
const BRACKETED_MARKER = /^\[(\d+)\]$/

/** All bracketed markers in one text node. */
const MARKER_PATTERN = /\[(\d+)\]/g

/**
 * A marker must be its own token: `[7]` is a footnote, `figure[7]` and
 * `[7]0` are not (bracketed ids inside words, Markdown link labels, etc.).
 */
const MARKER_BOUNDARY = /[\s.,;:!?()[\]{}"“”'’\-–—*_]/

/** Containers whose `<sup>` numbers are verse labels, not footnotes. */
const VERSE_CONTAINERS = '.biblical-quote-table, .hebQuote, .enQuote'

/** The footnote number a marker string carries, or `0`. */
export function markerIdFromText(text: string | null | undefined): number {
	const value = (text || '').trim()
	const match = value.match(BRACKETED_MARKER)
	return match ? Number(match[1]) : 0
}

/** The footnote number a superscript carries, or `0`. */
export function markerIdFromSup(sup: Element): number {
	return markerIdFromText(sup.textContent)
}

/** True when a `<sup>` sits where the site prints verse numbers instead of footnotes. */
export function isVerseLabelContainer(element: Element): boolean {
	return Boolean(element.closest(VERSE_CONTAINERS))
}

function makeMarkerSup(id: number, ownerDocument?: Document): HTMLElement {
	const doc = ownerDocument ?? elementDocument()
	const sup = doc.createElement('sup')
	sup.textContent = `[${id}]`
	return sup
}

function elementDocument(): Document {
	if (typeof document === 'undefined') throw new Error('footnote markers require a DOM')
	return document
}

/**
 * Normalize every footnote reference in `root` to `<sup>[N]</sup>`.
 *
 * Only markers whose id exists in `ids` are touched — an article that cites a
 * note the CMS never imported keeps its text instead of gaining a dead trigger.
 * Returns the number of nodes rewired.
 */
export function promoteFootnoteMarkers(root: ParentNode, ids: Set<number>): number {
	if (!ids.size) return 0
	let changed = 0

	// 1. Legacy anchors: the visible marker may be the anchor text, a nested
	//    <sup>, or absent entirely (the export's `name="_ftnrefN"` back-link).
	for (const anchor of Array.from(root.querySelectorAll(LEGACY_ANCHOR_SELECTOR))) {
		if (anchor.closest('.footnotes-section')) continue
		const visibleId = markerIdFromText(anchor.textContent)
		const hrefId = Number((anchor.getAttribute('href') || '').match(/(\d+)/)?.[1] || 0)
		const id = visibleId || hrefId
		if (!id || !ids.has(id)) continue

		const nested = anchor.querySelector('sup')
		if (nested) {
			// `<a href="#_ftn6"><sup>[6]</sup></a>` — keep the superscript, drop the dead link.
			anchor.replaceWith(nested)
		} else if (visibleId) {
			anchor.replaceWith(makeMarkerSup(id, anchor.ownerDocument))
		} else {
			// An empty back-link describing a marker that already renders elsewhere.
			anchor.remove()
		}
		changed++
	}

	// 2. Bare markers in text nodes (`… Kaʿba.[8]`).
	const walker = root.ownerDocument
		? root.ownerDocument.createTreeWalker(root as Node, 4 /* NodeFilter.SHOW_TEXT */)
		: elementDocument().createTreeWalker(root as Node, 4 /* NodeFilter.SHOW_TEXT */)
	const textNodes: Text[] = []
	for (let node = walker.nextNode(); node; node = walker.nextNode()) {
		const text = node as Text
		const parent = text.parentElement
		if (!parent) continue
		if (parent.closest('sup, a, .footnotes-section, script, style, code, pre')) continue
		if (/\[\d+\]/.test(text.data)) textNodes.push(text)
	}

	for (const node of textNodes) {
		const data = node.data
		let fragment: DocumentFragment | null = null
		let cursor = 0
		MARKER_PATTERN.lastIndex = 0
		for (let match = MARKER_PATTERN.exec(data); match; match = MARKER_PATTERN.exec(data)) {
			const id = Number(match[1])
			if (!ids.has(id)) continue
			const before = match.index > 0 ? data[match.index - 1] : ''
			const after = data[match.index + match[0].length] || ''
			if (before && !MARKER_BOUNDARY.test(before)) continue
			if (after && !MARKER_BOUNDARY.test(after)) continue
			fragment ??= elementDocument().createDocumentFragment()
			fragment.append(data.slice(cursor, match.index), makeMarkerSup(id, node.ownerDocument ?? undefined))
			cursor = match.index + match[0].length
			changed++
		}
		if (!fragment || !node.parentNode) continue
		fragment.append(data.slice(cursor))
		node.parentNode.replaceChild(fragment, node)
	}

	return changed
}
